import { EMOTE_ACTIONS, EMOTE_EMOJI } from "./table.ts";

/**
 * Entitlement (ADR-114 decision 6.3): each emote belongs to one named set, and an account holds a list of set names.
 * The gateway refuses an `emote` whose id is outside the account's sets, and the page draws the wheel from the same
 * answer over the `emotes` it was sent.
 */

/**
 * The most set names `emotes` lists for one account.
 */
export const MAX_EMOTE_SETS = 16;

/**
 * The longest set name.
 */
export const MAX_EMOTE_SET_CHARS = 32;

/**
 * A set's name as the wire carries it: lower-case letters, digits and hyphens, so a set a later server names is a
 * value `entitledEmotes` reads as holding nothing rather than a malformed frame.
 */
export const EMOTE_SET_PATTERN = /^[a-z0-9-]+$/;

/**
 * The emotes a list of sets holds: the ids of its actions and the ids of its emoji, apart, since ids are per kind.
 */
export interface EntitledEmotes {
  readonly actions: ReadonlySet<number>;
  readonly emoji: ReadonlySet<number>;
}

/**
 * The emotes held by the sets named: for each kind, the union of the ids of that kind in those sets.
 *
 * @param sets The set names an account holds, in any order; a name twice counts once.
 * @returns The action ids and the emoji ids held. A set name the table does not know adds nothing.
 * @example
 * entitledEmotes(["base"]).emoji.has(12);
 * // true
 * entitledEmotes(["a-later-set"]).actions.size;
 * // 0
 */
export function entitledEmotes(sets: readonly string[]): EntitledEmotes {
  const named = new Set(sets);
  const actions = new Set<number>();
  const emoji = new Set<number>();
  for (const row of EMOTE_ACTIONS) {
    if (named.has(row.set)) {
      actions.add(row.id);
    }
  }
  for (const row of EMOTE_EMOJI) {
    if (named.has(row.set)) {
      emoji.add(row.id);
    }
  }
  return { actions, emoji };
}
